import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useAuth } from "../auth";
import { TripCtx } from "./TripLayout";
import { MessageCircle, Mail, LogOut } from "lucide-react";

export default function MorePage() {
  const { tripId } = useParams();
  const { API, authHeaders, logout } = useAuth();
  const { trip } = useContext(TripCtx);
  const [logistics, setLogistics] = useState([]);
  const [reservations, setReservations] = useState([]);

  useEffect(() => {
    axios.get(`${API}/trips/${tripId}/logistics`, { headers: authHeaders }).then((r) => setLogistics(r.data));
    axios.get(`${API}/trips/${tripId}/reservations`, { headers: authHeaders }).then((r) => setReservations(r.data));
  }, [tripId]);

  const grouped = logistics.reduce((acc, l) => { (acc[l.category] = acc[l.category] || []).push(l); return acc; }, {});
  const labels = { transfer: "Transfers", access: "Access", contact: "Contacts" };

  return (
    <div data-testid="more-page" className="py-4">
      <p className="tcm-eyebrow">Trip essentials</p>
      <h1 className="font-display text-4xl mt-2">More</h1>
      <hr className="tcm-gold-rule w-16 my-6" />

      <section className="mb-10">
        <p className="tcm-eyebrow mb-4">Trip</p>
        <div className="grid grid-cols-2 gap-4">
          <a data-testid="more-explore" href={`/trips/${trip.id}/explore`} className="tcm-double-frame block">
            <div className="tcm-inner p-5 text-center">
              <p className="font-display text-xl">Explore</p>
              <p className="tcm-eyebrow mt-1">Curated recs</p>
            </div>
          </a>
          <a data-testid="more-experiences" href={`/trips/${trip.id}/experiences`} className="tcm-double-frame block">
            <div className="tcm-inner p-5 text-center">
              <p className="font-display text-xl">Experiences</p>
              <p className="tcm-eyebrow mt-1">Private productions</p>
            </div>
          </a>
          <a data-testid="more-diary" href={`/trips/${trip.id}/diary`} className="tcm-double-frame block">
            <div className="tcm-inner p-5 text-center">
              <p className="font-display text-xl">{trip.archived ? "Memories" : "Diary"}</p>
              <p className="tcm-eyebrow mt-1">Photos & notes</p>
            </div>
          </a>
          <a data-testid="more-documents" href={`/trips/${trip.id}/documents`} className="tcm-double-frame block">
            <div className="tcm-inner p-5 text-center">
              <p className="font-display text-xl">Documents</p>
              <p className="tcm-eyebrow mt-1">Visas · Insurance · Vouchers</p>
            </div>
          </a>
          <a data-testid="more-chat-tile" href={`/trips/${trip.id}/concierge`} className="tcm-double-frame block">
            <div className="tcm-inner p-5 text-center">
              <p className="font-display text-xl">Chat</p>
              <p className="tcm-eyebrow mt-1">Concierge inbox</p>
            </div>
          </a>
        </div>
      </section>

      <section className="mb-10">
        <p className="tcm-eyebrow mb-4">Concierge</p>
        <div className="grid grid-cols-2 gap-4">
          <a data-testid="more-chat" href={`/trips/${trip.id}/concierge`} className="tcm-double-frame block">
            <div className="tcm-inner p-5 text-center">
              <MessageCircle size={20} strokeWidth={1.25} className="mx-auto" />
              <p className="font-display text-xl mt-2">Open chat</p>
              <p className="tcm-eyebrow mt-1">In-app</p>
            </div>
          </a>
          <a data-testid="email-link" href="mailto:concierge@the-curated-mile.com" className="tcm-double-frame block">
            <div className="tcm-inner p-5 text-center">
              <Mail size={20} strokeWidth={1.25} className="mx-auto" />
              <p className="font-display text-xl mt-2">Email</p>
              <p className="tcm-eyebrow mt-1">Concierge inbox</p>
            </div>
          </a>
        </div>
      </section>

      {reservations.length > 0 && (
        <section className="mb-10">
          <p className="tcm-eyebrow mb-4">Reservations</p>
          <div className="space-y-3">
            {reservations.map((r) => (
              <div key={r.id} className="border-t border-[color:var(--tcm-navy-15)] pt-3">
                <div className="flex justify-between">
                  <p className="font-display text-xl">{r.title}</p>
                  <span className="tcm-chip">{r.status}</span>
                </div>
                {r.reference && <p className="tcm-eyebrow mt-1">Ref · {r.reference}</p>}
                {r.date && <p className="text-sm mt-1 text-[color:var(--tcm-navy-70)]">{r.date}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {Object.keys(grouped).map((cat) => (
        <section key={cat} className="mb-10">
          <p className="tcm-eyebrow mb-4">{labels[cat] || cat}</p>
          <div className="space-y-3">
            {grouped[cat].map((l) => (
              <div key={l.id} className="border-t border-[color:var(--tcm-navy-15)] pt-3">
                <p className="font-display text-xl">{l.title}</p>
                <p className="text-sm mt-1 whitespace-pre-wrap">{l.details}</p>
              </div>
            ))}
          </div>
        </section>
      ))}

      <button data-testid="signout-more" onClick={logout} className="tcm-btn-outline inline-flex items-center gap-2 mt-6">
        <LogOut size={12} /> Sign out
      </button>

      <div className="mt-12 pt-6 border-t border-[color:var(--tcm-navy-15)] text-center">
        <a data-testid="back-to-marketing-more" href="https://www.the-curated-mile.com"
           className="tcm-eyebrow underline decoration-[#C9A961] underline-offset-4">
          ← the-curated-mile.com
        </a>
      </div>
    </div>
  );
}
